import React, {useContext, PropsWithChildren, useState, useEffect, useRef} from 'react';
import {UserLocation, useLocation} from './useLocationContext';

type SetMapLocation = (value: UserLocation) => void;

const MapLocationContext = React.createContext<{
  location?: UserLocation;
  set: SetMapLocation;
}>({
  set: _ => {},
});

const MapLocationProvider = ({children, ...props}: PropsWithChildren<{}>) => {
  const {location} = useLocation();
  const [state, setState] = useState<UserLocation | undefined>(undefined);
  // Once a caller explicitly picks a map location (e.g. tapping a beach),
  // stop passively overwriting it with the user's GPS — otherwise a late
  // GPS fix snaps the map back to the user while they're looking elsewhere.
  const explicitlySet = useRef(false);

  const set: SetMapLocation = value => {
    explicitlySet.current = true;
    setState(value);
  };

  useEffect(() => {
    if (explicitlySet.current) return;
    setState(location);
  }, [location]);

  return (
    <MapLocationContext.Provider value={{location: state, set}} {...props}>
      {children}
    </MapLocationContext.Provider>
  );
};

const useMapLocation = () => useContext(MapLocationContext);

export {MapLocationProvider, useMapLocation};
